import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Copy,
  Check,
  Mail,
  MessageCircle,
  MessageSquare,
  Smartphone,
  Twitter,
  Facebook,
  Linkedin,
  QrCode,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ShareChannel,
  ShareContext,
  buildChannelHref,
  buildShareUrl,
  defaultShareText,
} from '@/lib/share';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  text?: string;
  url: string;
  context?: ShareContext;
}

interface ChannelDef {
  id: ShareChannel;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  className: string;
}

const CHANNELS: ChannelDef[] = [
  { id: 'messenger', label: 'Messenger', icon: MessageCircle, className: 'hover:border-primary/40' },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, className: 'hover:border-primary/40' },
  { id: 'sms', label: 'SMS', icon: Smartphone, className: 'hover:border-primary/40' },
  { id: 'email', label: 'Email', icon: Mail, className: 'hover:border-primary/40' },
  { id: 'twitter', label: 'X', icon: Twitter, className: 'hover:border-primary/40' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, className: 'hover:border-primary/40' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, className: 'hover:border-primary/40' },
];

export function ShareDialog({ open, onOpenChange, title, text, url, context = 'generic' }: ShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const shareText = text || defaultShareText(context, title);
  const copyUrl = buildShareUrl(url, 'copy');

  useEffect(() => {
    if (!showQr) return;
    const qrUrl = buildShareUrl(url, 'copy');
    QRCode.toDataURL(qrUrl, { width: 240, margin: 1 })
      .then(setQrDataUrl)
      .catch((err) => {
        console.warn('[ShareDialog] QR generation failed', err);
        setQrDataUrl(null);
        toast.error('Kunne ikke generere QR-kode');
      });
  }, [showQr, url]);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      setShowQr(false);
      setQrDataUrl(null);
    }
  }, [open]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyUrl);
      setCopied(true);
      toast.success('Link kopieret');
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Kunne ikke kopiere linket');
    }
  };

  const handleChannel = (ch: ChannelDef) => {
    const channelUrl = buildShareUrl(url, ch.id);
    const href = buildChannelHref(ch.id, { title, text: shareText, url: channelUrl });
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Del denne side</DialogTitle>
          <DialogDescription className="line-clamp-2">{title}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Kopier link */}
          <div className="flex gap-2">
            <Input value={copyUrl} readOnly className="text-xs" onFocus={(e) => e.currentTarget.select()} />
            <Button
              type="button"
              onClick={handleCopy}
              variant={copied ? 'default' : 'outline'}
              className="shrink-0 gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Kopieret' : 'Kopier'}
            </Button>
          </div>

          {/* Kanaler */}
          <div className="grid grid-cols-4 gap-2">
            {CHANNELS.map((ch) => {
              const Icon = ch.icon;
              return (
                <button
                  key={ch.id}
                  type="button"
                  onClick={() => handleChannel(ch)}
                  className={`flex flex-col items-center gap-1.5 rounded-lg border border-border bg-card p-3 text-xs font-medium text-foreground transition-all hover:bg-secondary hover:-translate-y-0.5 ${ch.className}`}
                >
                  <Icon className="w-5 h-5 text-primary" />
                  {ch.label}
                </button>
              );
            })}
          </div>

          {/* QR */}
          <div className="border-t border-border pt-3">
            <button
              type="button"
              onClick={() => setShowQr((v) => !v)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <QrCode className="w-4 h-4" />
              {showQr ? 'Skjul QR-kode' : 'Vis QR-kode'}
            </button>
            {showQr && (
              <div className="mt-3 flex flex-col items-center gap-2">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR-kode til deling" className="rounded-md border border-border bg-white p-2" />
                ) : (
                  <div className="h-[240px] w-[240px] animate-pulse rounded-md bg-secondary" />
                )}
                <p className="text-xs text-muted-foreground">Scan med mobilen for at åbne linket</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
